import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaSearch, FaTimes, FaStar } from 'react-icons/fa';
import projects from '../data/projects';
import './Projects.css';

const Projects = ({ t, language }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Get all unique technologies for filter
  const allTechnologies = useMemo(() => {
    const techSet = new Set();
    projects.forEach(project => {
      project.technologies.forEach(tech => techSet.add(tech));
    });
    return ['all', ...Array.from(techSet)].sort();
  }, []);

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch = project.title[language].toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.shortDescription[language].toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.technologies.some(tech => tech.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFilter = selectedFilter === 'all' || 
                           project.technologies.includes(selectedFilter);
      
      return matchesSearch && matchesFilter;
    });
  }, [searchTerm, selectedFilter, language]);

  const featuredProjects = filteredProjects.filter(p => p.featured);
  const otherProjects = filteredProjects.filter(p => !p.featured);

  return (
    <section className="projects" id="projects">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title neon-text">{t.projects.title}</h2>
          <p className="projects-subtitle">{t.projects.subtitle}</p>

          {/* Search and Filter */}
          <div className="projects-controls">
            {/* Search Bar */}
            <div className="search-bar neon-border">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder={t.projects.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Technology Filter */}
            <div className="tech-filter">
              {allTechnologies.map(tech => (
                <motion.button
                  key={tech}
                  className={`filter-btn ${selectedFilter === tech ? 'active' : ''}`}
                  onClick={() => setSelectedFilter(tech)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {tech}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Featured Projects */}
          {featuredProjects.length > 0 && (
            <div className="featured-section" id="featured-projects">
              <h3 className="featured-title neon-text-green">
                <FaStar /> {t.projects.featured}
              </h3>
              <div className="projects-grid featured-grid">
                {featuredProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    language={language}
                    t={t}
                    onClick={() => setSelectedProject(project)}
                    index={index}
                    featured={true}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Projects */}
          {otherProjects.length > 0 && (
            <div className="all-projects-section" id="other-projects">
              {featuredProjects.length > 0 && (
                <h3 className="all-projects-title neon-text">
                  {t.projects.all}
                </h3>
              )}
              <div className="projects-grid">
                {otherProjects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    language={language}
                    t={t}
                    onClick={() => setSelectedProject(project)}
                    index={index + featuredProjects.length}
                    featured={false}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {filteredProjects.length === 0 && (
            <motion.div 
              className="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p>{t.common.noResults}</p>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            language={language}
            t={t}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

// Project Card Component (also used by Tools)
export const ProjectCard = ({ project, language, t, onClick, index, featured, className = '' }) => {
  return (
    <motion.div
      className={`project-card ${featured ? 'featured-card' : ''} ${className} neon-border`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ 
        y: -10,
        boxShadow: 'var(--glow-blue)'
      }}
      viewport={{ once: true }}
      onClick={onClick}
    >
      {featured && (
        <div className="featured-badge">
          <FaStar /> {t.projects.featured}
        </div>
      )}
      
      <h3 className="project-title">{project.title[language]}</h3>
      <p className="project-description">{project.shortDescription[language]}</p>
      
      <div className="project-tech">
        {project.technologies.slice(0, 4).map((tech, i) => (
          <span key={i} className="tech-badge">{tech}</span>
        ))}
        {project.technologies.length > 4 && (
          <span className="tech-badge">+{project.technologies.length - 4}</span>
        )}
      </div>

      <div className="project-links">
        <a 
          href={project.github}
          target="_blank"
          rel="noopener noreferrer"
          className="project-link"
          onClick={(e) => e.stopPropagation()}
        >
          <FaGithub /> GitHub
        </a>
        <button className="project-link read-more">
          {t.projects.readMore} →
        </button>
      </div>
    </motion.div>
  );
};

// Project Modal Component (also used by Tools)
export const ProjectModal = ({ project, language, t, onClose }) => {
  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content neon-border"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="modal-body">
          <h2 className="modal-title neon-text">{project.title[language]}</h2>
          
          <div className="modal-description">
            {project.fullDescription[language].split('\n\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>

          <div className="modal-section">
            <h3 className="modal-section-title neon-text-green">
              {t.projects.technologies}
            </h3>
            <div className="modal-tech">
              {project.technologies.map((tech, i) => (
                <span key={i} className="tech-badge-large">{tech}</span>
              ))}
            </div>
          </div>

          <div className="modal-section">
            <h3 className="modal-section-title neon-text-green">
              {t.projects.highlights}
            </h3>
            <ul className="highlights-list">
              {project.highlights[language].map((highlight, i) => (
                <li key={i}>{highlight}</li>
              ))}
            </ul>
          </div>

          <div className="modal-actions">
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="neon-button"
            >
              <FaGithub /> {t.projects.viewCode}
            </a>
            {project.liveDemo && (
              <a
                href={project.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                className="neon-button neon-button-secondary"
              >
                <FaExternalLinkAlt /> {t.projects.viewDemo}
              </a>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Projects;
